import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PMAvatar, PMButton, PMCalendar, PMCheckbox, PMIcon, PMKanban, PMSegmented, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { usePeople, useReminders, type Reminder } from "@/data/queries";
import { useMutations } from "@/data/mutations";
import { Page } from "@/ui/Page";
import { Character } from "@/ui/Character";
import { avatarColor, fullName } from "@/ui/format";
import { TaskComposer } from "./TaskComposer";
import { TaskDetail } from "./TaskDetail";
import { addDays, BUCKET_LABEL, bucket, dueLabel, isClosed, splitDetails, typeOf, type Bucket } from "./taskModel";
import "./tasks.css";

const SCOPE_TITLE: Record<string, string> = { mine: "My tasks", active: "Active tasks", all: "All tasks", done: "Completed tasks" };
const ORDER: Bucket[] = ["overdue", "today", "week", "later", "done"];

// Tasks as an inbox: list on the left, the selected task on the right.
// Every state is in the URL (?scope, ?view, ?task, ?new) so modules, links
// and the back button all land in the right place.
export function TasksPage() {
  const nav = useNavigate();
  const { userId } = useApp();
  const search = useSearch({ from: "/t/$teamId/tasks" });
  const all = useReminders();
  const people = usePeople().data;
  const m = useMutations();
  const set = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: !("task" in patch) } as any);
  const scope = search.scope || "all";
  const view = search.view || "list";
  const [showDone, setShowDone] = useState(scope === "done");
  useEffect(() => setShowDone(scope === "done"), [scope]);

  const tasks = useMemo(() => all.data.filter((t) => {
    if (scope === "mine") return !isClosed(t) && (!t.assigned_user_id || t.assigned_user_id === userId);
    if (scope === "active") return !isClosed(t) && (t.reminder_date || addDays(0)) <= addDays(7);
    if (scope === "done") return isClosed(t);
    return true;
  }), [all.data, scope, userId]);

  const groups = useMemo(() => {
    const g: Record<Bucket, Reminder[]> = { overdue: [], today: [], week: [], later: [], done: [] };
    for (const t of tasks) g[bucket(t)].push(t);
    g.done.sort((a, b) => (b.completed_at || b.canceled_at || "").localeCompare(a.completed_at || a.canceled_at || ""));
    return g;
  }, [tasks]);
  const open = tasks.filter((t) => !isClosed(t));
  const dueTodayLeft = (exclude: string) => all.data.filter((x) => x.id !== exclude && !isClosed(x) && (x.reminder_date || addDays(0)) <= addDays(0)).length;
  const toggle = (t: Reminder) => (isClosed(t) ? m.reinstateReminder(t.id) : m.completeReminder(t.id, dueTodayLeft(t.id)));
  const select = (id: string | undefined) => set({ task: id });

  const row = (t: Reminder) => {
    const { title, notes } = splitDetails(t.details);
    const who = people.find((p) => p.user_id === t.assigned_user_id);
    const type = typeOf(t);
    const b = bucket(t);
    return (
      <li key={t.id} className={`ti ${isClosed(t) ? "is-done" : ""} ${search.task === t.id ? "is-sel" : ""}`}>
        <PMCheckbox checked={isClosed(t)} onChange={() => toggle(t)} aria-label={isClosed(t) ? `Reopen ${title}` : `Complete ${title}`} />
        <button className="ti-main" onClick={() => select(t.id)}>
          <span className="ti-title">{title}</span>
          <span className="ti-meta">
            <span className={`ti-type ${type.tone}`}><PMIcon name={type.icon} size={13} />{type.label}</span>
            {t.location_label && <span>{t.location_label}</span>}
            {notes && <span className="ti-hasnotes" title="Has notes"><PMIcon name="document-outline" size={13} /></span>}
          </span>
        </button>
        <span className={`ti-due ti-due--${b}`}>{dueLabel(t)}</span>
        {who ? <PMAvatar name={fullName(who)} size={26} color={avatarColor(who.user_id)} /> : <span className="ti-noav" />}
      </li>
    );
  };

  const card = (t: Reminder) => ({ id: t.id, title: splitDetails(t.details).title, subtitle: [t.location_label, dueLabel(t)].filter(Boolean).join(" · ") });

  return (
    <Page
      title={SCOPE_TITLE[scope] ?? "Tasks"}
      subtitle={scope === "done" ? `${groups.done.length} completed` : `${open.length} open${groups.overdue.length ? `, ${groups.overdue.length} overdue` : ""}${groups.today.length ? `, ${groups.today.length} due today` : ""}`}
      actions={<div className="row">
        <PMSegmented value={view} onChange={(v: string) => set({ view: v === "list" ? undefined : v })} options={[{ value: "list", label: "List" }, { value: "board", label: "Board" }, { value: "calendar", label: "Calendar" }]} />
        <PMButton icon="plus" onClick={() => set({ new: "1" })}>New task</PMButton>
      </div>}
      wide
    >
      {all.isLoading && !all.data.length ? (
        <div className="skeleton-stack">{[0, 1, 2].map((i) => <PMSkeleton key={i} shape="rect" height={64} />)}</div>
      ) : !all.data.length ? (
        <Character name="propal" size={110} title="No tasks yet" line="Follow-ups from visits and tasks created here show up in this list." action={<PMButton icon="plus" onClick={() => set({ new: "1" })}>New task</PMButton>} />
      ) : (
        <div className={`inbox ${search.task ? "has-detail" : ""}`}>
          <div className="inbox-list">
            {view === "list" && (
              tasks.length === 0 ? (
                <Character name="happy-propal" size={96} title={scope === "done" ? "Nothing completed yet" : "All clear"} line={scope === "mine" ? "Nothing open is assigned to you." : scope === "active" ? "Nothing due in the next seven days." : "Nothing here right now."} />
              ) : ORDER.filter((b) => groups[b].length && (b !== "done" || showDone || scope === "done")).map((b) => (
                <section key={b} className={`tgroup tgroup--${b}`}>
                  <h3 className="tgroup-h">{BUCKET_LABEL[b]} <span>{groups[b].length}</span></h3>
                  <ul className="tlist">{groups[b].slice(0, b === "done" ? 30 : undefined).map(row)}</ul>
                </section>
              ))
            )}
            {view === "list" && groups.done.length > 0 && scope !== "done" && (
              <button className="linkbtn tshow" onClick={() => setShowDone((s) => !s)}>{showDone ? "Hide completed" : `Show ${groups.done.length} completed`}</button>
            )}
            {view === "board" && (
              <PMKanban
                columns={[
                  { id: "overdue", title: "Overdue", color: "var(--pm-accent-critical)", cards: groups.overdue.map(card) },
                  { id: "week", title: "Due this week", color: "var(--pm-warning)", cards: [...groups.today, ...groups.week].map(card) },
                  { id: "later", title: "Later", color: "var(--pm-info)", cards: groups.later.map(card) },
                  { id: "done", title: "Done", color: "var(--pm-success)", cards: groups.done.slice(0, 20).map(card) },
                ]}
                onCardClick={(c: { id?: string | number }) => select(String(c.id))}
              />
            )}
            {view === "calendar" && (
              <div className="card card-pad">
                <PMCalendar
                  title="Task calendar"
                  events={tasks.filter((t) => t.reminder_date).map((t) => ({ id: t.id, date: t.reminder_date!, type: isClosed(t) ? "done" : typeOf(t).key, title: splitDetails(t.details).title, subtitle: t.location_label ?? undefined, time: t.reminder_time?.slice(0, 5) }))}
                  categories={{ todo: { label: "To-do", color: "var(--pm-primary)" }, follow: { label: "Follow-up", color: "var(--pm-info)" }, appt: { label: "Appointment", color: "var(--pm-secondary)" }, done: { label: "Done", color: "var(--pm-success)" } }}
                  onEventClick={(e: any) => select(String(e.id))}
                />
              </div>
            )}
          </div>
          {search.task && (
            <div className="inbox-detail">
              <TaskDetail id={search.task} onClose={() => select(undefined)} remainingToday={dueTodayLeft(search.task)} />
            </div>
          )}
        </div>
      )}

      <TaskComposer open={search.new === "1"} onClose={() => set({ new: undefined })} onCreated={(id) => nav({ to: ".", search: { ...search, new: undefined, task: id } } as any)} />
    </Page>
  );
}
